'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'

export interface NavigationItem {
  name: string
  href: string
}

interface NavigationProps {
  navigationItems: NavigationItem[]
  logo: {
    src: string
    alt: string
    width?: string
    height?: string
  }
}

export default function Navigation({
  navigationItems,
  logo
}: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 20)
    }
    
    window.addEventListener('scroll', handleScroll)
    
    // Initial check in case page is loaded at a scrolled position
    handleScroll()
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <header className={`${hasScrolled ? 'fixed border-t-0' : 'absolute border-t-8'} inset-x-0 top-0 left-0 right-0 border-l-8 border-r-8 border-b-0 border-white md:border-l-[16px] md:border-r-[16px] z-50 transition-all duration-300 ease-in-out`}>
      <nav 
        aria-label="Global" 
        className={`flex items-center justify-between p-6 lg:px-8 tracking-wider ${
          hasScrolled 
            ? 'bg-white shadow-md py-4 md:py-4 border-t-0' 
            : 'bg-transparent md:py-6 border-t-0'
        } transition-all duration-300`}
      >
        <div className="flex lg:flex-1">
          <a href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Trusted</span>
            <img 
              alt={logo.alt}
              src={logo.src}
              className="h-auto w-[145px]"
              style={{ width: logo.width }}
              width={logo.width}
              height={logo.height}
            />
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-teal-950 hover:text-zing-400 transition-colors duration-200"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-12">
          {navigationItems?.map((item) => (
            <a key={item.name} href={item.href} className="text-sm/6 font-semibold text-teal-950 hover:text-happy-600 transition-colors duration-200">
              {item.name}
            </a>
          ))}
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <a
            href="/contact"
            className="button-base-primary button-style-teal"
          >
            Talk to us <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">&rarr;</span>
          </a>
          {/* <a href="#" className="text-sm/6 font-semibold text-happy-600 hover:text-teal-950 transition-colors duration-200 group">
            Talk to us <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">&rarr;</span>
          </a> */}
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-teal-950 px-12 py-12 sm:max-w-sm sm:ring-1 sm:ring-white/10">
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Trusted</span>
              <img
                alt=""
                src="/images/logos/the-trusted-smile-white.svg"
                className="h-8 w-auto"
              />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-mt-3.5 -mr-2.5 rounded-md text-white hover:text-zing-400 transition-colors duration-200"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/25">
              <div className="space-y-2 py-6">
                {navigationItems?.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-sm tracking-wider font-semibold text-white hover:bg-white/5 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              <div className="py-6">
                <a
                    href="/contact"
                    className="button-base-primary button-style-happy"
                  >
                    Talk to us
                  </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
} 